import {closeCurrentDialog, openDialog, showNotification} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isEmpty, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, valueOrPlaceholder} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {DmsDataGrid, type DmsDataGridProps} from '../DmsDataGrid/DmsDataGrid';
import {EmailWindow} from '../SendCustomerEmailForm/components/EmailWindow';
import {SentMessagePreviewDialog} from '../SentMessagePreviewDialog/SentMessagePreviewDialog';

interface CommunicationActivityLogDatagridProps
  extends RequiredTestIdProps,
    Pick<DmsDataGridProps, 'maximization'> {
  queryModifier: (filter: QueryFilterObject) => QueryFilterObject;
  presetInstanceId: string;
}

type EntityColumnValue = {
  id: string;
  label: string;
};

type AttachmentColumnValue = {
  id: string;
  title: string;
  contentType: string;
  remoteUrl: string;
};

type ChannelColumnValue = {
  key: 'sms' | 'email' | 'whatsapp' | 'call';
};

const PREDEFINED_COLUMNS = [
  'content',
  'contact',
  'senderId',
  'createdAt',
  'attachments',
  'channel',
] as const;

export function CommunicationActivityLogDatagrid(props: CommunicationActivityLogDatagridProps) {
  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    getColumnData,
  }) => {
    if (actionKey !== 'preview') {
      return;
    }

    const content = getColumnData<string>('content')?.value;
    const channel = getColumnData<ChannelColumnValue>('channel')?.value?.key;

    // A call row has no content to preview.
    if (channel === 'call') {
      return;
    }

    if (isNil(content) || isEmpty(content)) {
      showNotification.error();
      return;
    }

    if (channel === 'sms' || channel === 'whatsapp') {
      openDialog(
        <Box height={150}>
          <EmailWindow htmlData={content} />
        </Box>,
        {
          title: match(channel)
            .with('whatsapp', () => i18n.t('general.labels.whatsapp'))
            .otherwise(() => i18n.t('entity.email.labels.smsSent')),
          disableBodyPadding: true,
          buttons: [{title: i18n.t('general.actions.close'), onClick: closeCurrentDialog}],
        }
      );

      return;
    }

    if (channel === 'email') {
      const contact = getColumnData<string>('contact')?.value;
      const sender = getColumnData<EntityColumnValue>('senderId')?.value?.label;
      const createdAt = getColumnData<string>('createdAt')?.value;
      const attachmentsData = getColumnData<AttachmentColumnValue[]>('attachments')?.value;
      // Subject will be added, once BE make it available in sprint 82
      // https://carvago.atlassian.net/browse/T20-107913
      const subject = EMPTY_PLACEHOLDER;

      openDialog(
        <SentMessagePreviewDialog
          attachments={mapAttachments(attachmentsData)}
          body={valueOrPlaceholder(content)}
          recipient={valueOrPlaceholder(contact)}
          sender={valueOrPlaceholder(sender)}
          sentAt={valueOrPlaceholder(createdAt)}
          subject={subject}
        />,
        {
          title: i18n.t('entity.email.labels.sentEmail'),
          size: 'xLarge',
          buttons: [
            {
              title: i18n.t('general.actions.close'),
              onClick: closeCurrentDialog,
              variant: 'secondary',
            },
          ],
        }
      );

      return;
    }

    showNotification.error();
  };

  return (
    <DmsDataGrid
      maximization={props.maximization}
      gridCode="messaging-customer-message-history"
      queryModifier={props.queryModifier}
      presetInstanceId={props.presetInstanceId}
      data-testid={props['data-testid']}
      actionCallback={actionCallback}
      predefinedColumns={PREDEFINED_COLUMNS}
    />
  );
}

function mapAttachments(
  attachmentsData: (Partial<AttachmentColumnValue> | undefined)[] | undefined
) {
  if (isNil(attachmentsData)) {
    return [];
  }

  return attachmentsData
    .filter(
      (attachment): attachment is AttachmentColumnValue =>
        isNotNil(attachment) &&
        isNotNil(attachment.id) &&
        isNotNil(attachment.title) &&
        isNotNil(attachment.remoteUrl) &&
        isNotNil(attachment.contentType)
    )
    .map((attachment) => ({
      id: attachment.id,
      title: attachment.title,
      remoteUrl: attachment.remoteUrl,
      contentType: attachment.contentType,
    }));
}
